import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Package, Users, ShoppingCart, AlertCircle, Loader2 } from 'lucide-react';
import VendorManagement from './VendorManagement';
import MaterialManagement from './MaterialManagement';
import PurchaseOrders from './PurchaseOrders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPProcurement = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, vendorsRes, materialsRes, posRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/procurement/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/procurement/vendors`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/procurement/materials`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/procurement/purchase-orders`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setVendors(vendorsRes.data);
      setMaterials(materialsRes.data);
      setPurchaseOrders(posRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchData();
    }
  }, [fetchData, activeTab]);

  if (loading && activeTab === 'overview') return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Procurement Management</h1>
          <p className="text-gray-600 mt-1">Vendors, materials, and purchase orders</p>
        </div>
      </div>

      <div className="mb-6 border-b">
        <div className="flex gap-4">
          <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 font-medium ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Overview</button>
          <button onClick={() => setActiveTab('vendors')} className={`pb-3 px-4 font-medium ${activeTab === 'vendors' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Vendors</button>
          <button onClick={() => setActiveTab('materials')} className={`pb-3 px-4 font-medium ${activeTab === 'materials' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Materials</button>
          <button onClick={() => setActiveTab('purchase-orders')} className={`pb-3 px-4 font-medium ${activeTab === 'purchase-orders' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>Purchase Orders</button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Vendors</p><p className="text-2xl font-bold">{stats?.total_vendors || 0}</p></div><Users className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Materials</p><p className="text-2xl font-bold">{stats?.total_materials || 0}</p></div><Package className="w-10 h-10 text-green-500" /></div></CardContent></Card>
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Low Stock</p><p className="text-2xl font-bold text-red-600">{stats?.low_stock_alerts || 0}</p></div><AlertCircle className="w-10 h-10 text-red-500" /></div></CardContent></Card>
            <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Pending POs</p><p className="text-2xl font-bold text-orange-600">{stats?.pending_orders || 0}</p></div><ShoppingCart className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Recent Vendors</h2><div className="space-y-2">{vendors.slice(0,5).map(v=>(<div key={v.id} className="p-2 bg-gray-50 rounded text-sm"><p className="font-medium">{v.name}</p><p className="text-xs text-gray-600">{v.category}</p></div>))}{!vendors.length && <p className="text-gray-500 text-center py-4 text-sm">No vendors</p>}</div></div></Card>
            <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Materials</h2><div className="space-y-2">{materials.slice(0,5).map(m=>(<div key={m.id} className="p-2 bg-gray-50 rounded text-sm flex justify-between"><span>{m.name}</span><span className={m.current_stock<m.min_stock_level?'text-red-600 font-bold':''}>{m.current_stock} {m.unit}</span></div>))}{!materials.length && <p className="text-gray-500 text-center py-4 text-sm">No materials</p>}</div></div></Card>
            <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Recent POs</h2><div className="space-y-2">{purchaseOrders.slice(0,5).map(po=>(<div key={po.id} className="p-2 bg-gray-50 rounded text-sm flex justify-between"><span>{po.po_number}</span><span className={po.status==='delivered'?'text-green-600':'text-orange-600'}>{po.status}</span></div>))}{!purchaseOrders.length && <p className="text-gray-500 text-center py-4 text-sm">No POs</p>}</div></div></Card>
          </div>
        </>
      )}

      {activeTab === 'vendors' && <VendorManagement />}
      {activeTab === 'materials' && <MaterialManagement />}
      {activeTab === 'purchase-orders' && <PurchaseOrders />}
    </div>
  );
};

export default ERPProcurement;
